import React from "react";
import { authStateManager } from "../utils/authStateManager";
import { handleRedirect } from "../pages/Login";
import {
  isNonProdBuild,
  isProductionModeOn,
  setProductionMode,
} from "../utils/productionMode";

/**
 * "Use production" switch on test builds (ENG-2040, ENG-2297). Shown to
 * anyone signed in: it only changes which API this unreleased build talks
 * to, and a user who can sign in here can sign in to prod with the same
 * credentials and gets the permissions they hold there. QA needs it on a
 * customer-level login to see the customer's view of production. Limiting
 * who can reach dev/redesign at all is ENG-2261. Off: a small pill in the
 * corner. On: a red frame and a bar that say the data is
 * live. Production mode IS production (ENG-2297): nothing is read-only here,
 * admin's permissions are the only gate. Switching signs out and back in against the
 * chosen API, then returns to the same page.
 */
export const switchApiEnvironment = (toProduction: boolean) => {
  setProductionMode(toProduction);
  authStateManager.clearAuthState();
  try {
    localStorage.removeItem("userInfo");
    localStorage.setItem(
      "redirectURL",
      window.location.pathname + window.location.search + window.location.hash
    );
  } catch {
    // Storage blocked: sign-in still works, it just lands on the home page.
  }
  handleRedirect();
};

export const ProductionModeBar = () => {
  if (!isNonProdBuild()) return null;
  const on = isProductionModeOn();
  if (on) {
    // Never on top of the page: the app is laid out full-screen, so a top
    // bar covered the tabs and the nav. A red frame round the window plus a
    // pill at the bottom keeps it unmissable without hiding anything.
    return (
      <>
        <div
          aria-hidden="true"
          data-testid="production-mode-frame"
          className="pointer-events-none fixed inset-0 z-[2000]"
          style={{ boxShadow: "inset 0 0 0 4px #B91C1C" }}
        />
        <div
          data-testid="production-mode-bar"
          role="alert"
          className="fixed bottom-3 left-1/2 z-[2001] flex -translate-x-1/2 items-center gap-3 rounded-full bg-[#B91C1C] px-4 py-1 font-mulish text-[13px] text-white shadow-lg"
        >
          <b>LIVE PRODUCTION DATA</b>
          <span>
            This is production. Saves, sync, arm/disarm, talk-down and alerts
            act on real sites and cameras.
          </span>
          <button
            type="button"
            data-testid="production-mode-off"
            onClick={() => switchApiEnvironment(false)}
            className="rounded-full border border-white px-2 py-[1px] font-bold hover:bg-white hover:text-[#B91C1C]"
          >
            Switch back to staging
          </button>
        </div>
      </>
    );
  }
  return (
    <button
      type="button"
      data-testid="production-mode-on"
      title="Test this build against live production data"
      onClick={() => switchApiEnvironment(true)}
      className="fixed bottom-3 right-3 z-[2000] rounded-full border border-[#B45309] bg-white px-3 py-1 font-mulish text-[12px] text-[#B45309] shadow hover:bg-[#FEF3C7]"
    >
      Staging data · Use production
    </button>
  );
};
